import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type Theme = 'dark' | 'light';

export const DEFAULT_THEME: Theme = 'dark';

export function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

/** Bounds for the bottom terminal's height, in pixels. */
export const TERMINAL_MIN_HEIGHT = 120;
export const TERMINAL_DEFAULT_HEIGHT = 260;

export interface UiState {
  /** The right-hand options drawer. Open by default so actions are discoverable. */
  readonly drawerOpen: boolean;
  readonly theme: Theme;
  /** The bottom terminal (stdin/stdout/stderr/build); its tab bar is always visible. */
  readonly terminalOpen: boolean;
  readonly terminalHeight: number;
}

export const initialUiState: UiState = {
  drawerOpen: true,
  theme: DEFAULT_THEME,
  terminalOpen: false,
  terminalHeight: TERMINAL_DEFAULT_HEIGHT,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUiState,
  reducers: {
    drawerToggled(state) {
      return { ...state, drawerOpen: !state.drawerOpen };
    },
    drawerClosed(state) {
      return { ...state, drawerOpen: false };
    },
    themeChanged(state, action: PayloadAction<Theme>) {
      return { ...state, theme: action.payload };
    },
    terminalOpened(state) {
      return { ...state, terminalOpen: true };
    },
    terminalClosed(state) {
      return { ...state, terminalOpen: false };
    },
    terminalToggled(state) {
      return { ...state, terminalOpen: !state.terminalOpen };
    },
    terminalResized(state, action: PayloadAction<number>) {
      return { ...state, terminalHeight: Math.max(TERMINAL_MIN_HEIGHT, Math.round(action.payload)) };
    },
  },
  selectors: {
    selectDrawerOpen: (state) => state.drawerOpen,
    selectTheme: (state) => state.theme,
    selectTerminalOpen: (state) => state.terminalOpen,
    selectTerminalHeight: (state) => state.terminalHeight,
  },
});

export const { drawerToggled, drawerClosed, themeChanged, terminalOpened, terminalClosed, terminalToggled, terminalResized } =
  uiSlice.actions;
export const { selectDrawerOpen, selectTheme, selectTerminalOpen, selectTerminalHeight } = uiSlice.selectors;
export { uiSlice };
